import { getIsCypressRunning } from "constants/constants";
import { getTokenInfo, useAuth0 } from "contexts/AuthManager/Auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import * as Sentry from "@sentry/react";
import useFlagsWithDefaults, {
  hasLDClientBeenIdentifiedAtom,
} from "utils/customHooks/useFlagsWithDefaults";
import { authExpiredAtom } from "./AuthenticationFailedIndicator";
import { atom, useAtom } from "jotai";
import { IdToken } from "@auth0/auth0-react";

export const hasAuthenticationCompletedAtom = atom<boolean>(false);

/** Handle authentication scenarios: (`??` = fallback)
 * - on mount -> silent reauth ?? login screen
 * - if the user is unauthorized -> login screen
 * - if the network goes offline and comes back online -> silent re-auth ?? login screen
 *
 */
export function AuthenticationHandler() {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasLDClientBeenIdentified] = useAtom(hasLDClientBeenIdentifiedAtom);
  // Handle going offline and back online
  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  const navigate = useNavigate();

  const { flightKickOutToLoginOnAuthExpired } = useFlagsWithDefaults();

  // try to authenticate
  const [, setAuthExpired] = useAtom(authExpiredAtom);
  const [, setHasAuthenticationCompleted] = useAtom(
    hasAuthenticationCompletedAtom
  );

  useEffect(() => {
    let id_token = "";
    let verbose: {
      id_token: string;
      access_token: string;
      refresh_token?: string;
      expires_in: number;
      scope?: string;
    } = {
      id_token: "",
      access_token: "",
      expires_in: 0,
    };
    const getNewToken = async () => {
      console.log("checking authentication...", Date.now());
      if (
        isLoading ||
        // if e2e tests are running, wait for API login before kicking them to login page
        (getIsCypressRunning() && !window.localStorage.getItem("auth0Cypress"))
      ) {
        console.log("tried to authenticate, but still loading...", Date.now());
        return;
      }
      try {
        // Obtain a new access token silently
        console.log("authenticating...", Date.now());
        verbose = await getAccessTokenSilently({
          detailedResponse: true,
        });
        id_token = verbose.id_token;
        console.log("authenticated!", Date.now());
      } catch (error) {
        console.error("Failed to refresh token:", error, Date.now());
        if (getIsCypressRunning()) {
          return;
        }
        // check the stored token's expiry, if it's expired, then re-authenticate
        console.log("checking stored token...", Date.now());
        const { idToken, isTokenValid } = getTokenInfo();
        if (!idToken || !isTokenValid) {
          // Handle token refresh failure, possibly by logging the user in again
          // await loginWithRedirect({
          //   appState: {
          //     targetUrl: window.location.pathname + window.location.search,
          //   },
          // });
          // instead of kicking them out, we'll navigate to a page that lets them log in again
          console.log("invalid token...", Date.now());
        }
        // setLoginFailed(true);
        if (getIsCypressRunning()) {
          return;
        }
        if (flightKickOutToLoginOnAuthExpired || !hasLDClientBeenIdentified) {
          console.log(
            "kicking out to login page...",
            {
              flightKickOutToLoginOnAuthExpired,
              hasLDClientBeenIdentified,
            },
            Date.now()
          );
          Sentry.captureException(new Error(`AUTH_FAILED_LOGINWITHREDIRECT`), {
            tags: {
              flightKickOutToLoginOnAuthExpired,
              hasLDClientBeenIdentified,
            },
          });
          return loginWithRedirect({
            appState: {
              targetUrl: window.location.pathname + window.location.search,
            },
          });
        } else {
          console.log("setting auth expired...", Date.now());
          setAuthExpired(true);
        }
        console.log("navigating to auth-failed...", Date.now());
        Sentry.captureException(new Error(`AUTH_FAILED_PAGE`));
        return navigate("/auth-failed");
      }

      // save it in local storage
      console.log("getting id_token claims...", Date.now());
      const claims = await getIdTokenClaims();
      if (id_token) {
        console.log(
          `id_token acquired (with length ${id_token.length})`,
          verbose,
          Date.now()
        );
        saveToken(id_token, claims);
      }
      console.log("authentication complete", Date.now());
      setHasAuthenticationCompleted(true);
    };

    let tokenRefreshInterval: number | null = null;

    if (isOnline) {
      // Re-authenticate if the device has reconnected to the internet
      console.log("device is online, re-authenticating...", Date.now());
      getNewToken();

      // Set up a timer to refresh the token before it expires
      tokenRefreshInterval = window.setInterval(getNewToken, ONE_HOUR_OF_MS);
    }

    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    }; // Clean up the interval on component unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    isLoading,
    isOnline,
    getIdTokenClaims,
    flightKickOutToLoginOnAuthExpired,
    setAuthExpired,
    // navigate, // don't include, else we excessively reauthenticate
    // hasLDClientBeenIdentified, // don't include, else we excessively reauthenticate
  ]);

  return null;
}

/** save the token in local storage */
function saveToken(token: string, tokenClaims?: IdToken) {
  console.log("saving token in local storage...", Date.now());
  // ? can only find the real id_token in this localStorage entry...
  try {
    const expiry =
      new Date(tokenClaims?.updated_at ?? 0).getTime() +
      (tokenClaims?.exp ?? 0);
    const { saveTokenInLocalStorage } = getTokenInfo();
    saveTokenInLocalStorage(token, `${expiry}`);
    console.log("token saved in local storage", Date.now());
  } catch (error) {
    console.log("failed to parse @@auth0spajs@@ tokens", error);
  }
}
